import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCompanyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { Building2, CheckCircle, XCircle, Loader2, Clock, Eye } from 'lucide-react';

export default function PendingCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const res = await adminCompanyAPI.getCompanies('PENDING');
      setCompanies(res.data.data?.content || []);
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setActionLoading((p) => ({ ...p, [id]: 'approve' }));
    try {
      await adminCompanyAPI.approveCompany(id);
      setCompanies((p) => p.filter((c) => c.id !== id));
      toast.success('Company approved!');
    } catch { toast.error('Failed to approve'); }
    finally { setActionLoading((p) => ({ ...p, [id]: null })); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) { toast.error('Please enter a reason'); return; }
    setActionLoading((p) => ({ ...p, [rejectModal]: 'reject' }));
    try {
      await adminCompanyAPI.rejectCompany(rejectModal, rejectReason);
      setCompanies((p) => p.filter((c) => c.id !== rejectModal));
      toast.success('Company rejected');
      setRejectModal(null); setRejectReason('');
    } catch { toast.error('Failed to reject'); }
    finally { setActionLoading((p) => ({ ...p, [rejectModal]: null })); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav
        title="Pending Companies"
        subtitle={companies.length + " awaiting approval"}
      />

      <div className="max-w-5xl mx-auto px-6 py-8">

        {companies.length === 0 ? (
          <div className="text-center py-24">
            <Clock className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-400">No pending companies</h3>
            <p className="text-sm text-gray-400 mt-1">All companies have been reviewed.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {companies.map((company) => (
              <div key={company.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Icon */}
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-800">{company.companyName}</h3>
                        {company.organizationType && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                            {company.organizationType}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{company.contactPersonName}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 mt-3 text-sm text-gray-600">
                        <span>📞 {company.contactPhone}</span>
                        <span>✉️ {company.contactEmail}</span>
                        {company.gstNumber && <span>🏢 GST: {company.gstNumber}</span>}
                        {company.city && (
                          <span>📍 {company.city}{company.state ? `, ${company.state}` : ''}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Registered {new Date(company.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                    {/* Actions */}
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => navigate(`/admin/company/${company.id}/review`)}
                      className="flex items-center gap-1.5 bg-slate-100 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-200 transition">
                      <Eye className="h-4 w-4" /> View Details
                    </button>
                    <button onClick={() => handleApprove(company.id)}
                      disabled={!!actionLoading[company.id]}
                      className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-green-700 transition disabled:opacity-60">
                      {actionLoading[company.id] === 'approve'
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <CheckCircle className="h-4 w-4" />}
                      Approve
                    </button>
                    <button
                      onClick={() => { setRejectModal(company.id); setRejectReason(''); }}
                      disabled={!!actionLoading[company.id]}
                      className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-red-100 transition disabled:opacity-60">
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="font-bold text-slate-800 mb-4">Reject Company</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
            <div className="flex gap-3 mt-4">
              <button onClick={handleRejectSubmit}
                disabled={actionLoading[rejectModal] === 'reject'}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {actionLoading[rejectModal] === 'reject'
                  ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Confirm Reject
              </button>
              <button onClick={() => setRejectModal(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}